import ButtonGroup from '@pelckmans/elementis/components/button-group';
import DropdownMenu from '@pelckmans/elementis/components/dropdown-menu';
import List from '@pelckmans/elementis/components/list';
import Details from '@pelckmans/elementis/icons/details';
import Duplicate from '@pelckmans/elementis/icons/duplicate';
import Edit from '@pelckmans/elementis/icons/edit';
import MaterialPresentationPresentModusIcon from '@pelckmans/elementis/icons/material-presentation-present-modus';
import Print from '@pelckmans/elementis/icons/print';
import Share from '@pelckmans/elementis/icons/share';
import Trash from '@pelckmans/elementis/icons/trash';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import type { ValuesOfObject } from '../../../@types/utils';
import type { Module } from '../../../api/module';
import type { SlideSet } from '../../../api/slide-set';
import type { Node, NodeWithoutDescription } from '../../../api/table-of-content';
import type { ANALYTICS_EVENT_SUBLOCATIONS } from '../../../constants/analyticsEvents';
import IdentityContext from '../../../contexts/IdentityContext';
import ModalContext from '../../../contexts/ModalContext';
import { useSlideSetAnalytics } from '../../../hooks/useSlideSetAnalytics';
import ActionMenuButton from '../../../shared-components/ActionMenuButton';
import ConfirmationModal from '../../../shared-components/ConfirmationModal';
import ShareModal from '../../../shared-components/share-modal/ShareModal';
import { buildGroupProps } from '../../../utils/elementisUtils';
import { buildCopySlideSetUrl, buildDoceoUrl } from '../../../utils/slideSet';
import SelectionContext from '../toc/_contexts/SelectionContext';
import MaterialPills from '../toc/components/MaterialPills';
import PinnedMaterialButton from '../toc/components/pinned-material/PinnedMaterialButton';
import { MaterialIcon } from './MaterialIcon';
import useModuleAccess from '../../../hooks/useModuleAccess';
import { MODULE_LICENSE } from '../../../core/access/module-access';
import AccountContext from '../../../contexts/AccountContext';

type Props = {
  node: Node | NodeWithoutDescription | undefined;
  module: Module;
  slideSet: SlideSet;
  canBePinned?: boolean;
  subLocationId?: ValuesOfObject<typeof ANALYTICS_EVENT_SUBLOCATIONS>;
  refetch: () => void;
  isShared?: boolean;
  onDelete?: (slideSet: SlideSet) => Promise<void>;
  showActionMenu?: boolean;
};

export default function SlideSetListItem({
  node: selectedNode,
  module: selectedModule,
  slideSet,
  canBePinned,
  subLocationId,
  refetch,
  isShared = false,
  onDelete,
  showActionMenu = true,
}: Props) {
  const { t } = useTranslation();
  const { captureOpened, captureEditOpened, captureOverviewOpened, captureDeleted } = useSlideSetAnalytics();

  const { identity } = useContext(IdentityContext);
  const {
    account: { provider },
  } = useContext(AccountContext);
  const { showSelection, selectedSlideSets, addSlideSets, removeSlideSets } = useContext(SelectionContext);
  const { showModal } = useContext(ModalContext);
  const { license, actsAs, canShare } = useModuleAccess(selectedModule);

  const slideSetUrl = buildDoceoUrl(slideSet, provider);
  const slideSetOverviewUrl = buildDoceoUrl(slideSet, provider, false);
  const slideSetEditUrl = buildDoceoUrl(slideSet, provider, true);
  const disabled =
    license === MODULE_LICENSE.TEACHER_NO_DOCUMENT ||
    license === MODULE_LICENSE.TEACHER_NO_DIGITAL_MANUAL ||
    license === MODULE_LICENSE.STUDENT_NO_DOCUMENT;

  const pillGroupProps = {
    'aria-label': t('PAGES.TOC_LEVEL.MATERIAL_PILLS.A11Y_LABEL'),
    children: (
      <MaterialPills
        isShared={isShared}
        isStudent={slideSet.targetGroup === 'STUDENT' && !slideSet.userId && identity.isTeacherAlike}
        isTeacher={slideSet.targetGroup === 'TEACHER' && !slideSet.userId}
        isInReview={slideSet.state === 'REVIEW'}
      />
    ),
    role: 'group',
  };

  const handleDelete = async (slideSet: SlideSet) => {
    if (!onDelete) return;
    await onDelete(slideSet);
    refetch();
    captureDeleted(slideSet.id, subLocationId);
  };

  const showDeleteModal = (slideSet: SlideSet) => {
    const BASE = 'PAGES.TOC_LEVEL.ACTION_MENU.OPTIONS.DELETE_SLIDE_SET.CONFIRMATION_MODAL';
    return showModal(
      <ConfirmationModal
        id={slideSet.id.toString()}
        headerTitle={t(`${BASE}.HEADER_TITLE`, {
          name: slideSet.name,
        })}
        title={t(`${BASE}.TITLE`)}
        message={t(`${BASE}.BODY`)}
        confirmationText={t(`${BASE}.CONFIRMATION_TEXT`)}
        onConfirm={() => handleDelete(slideSet)}
      />,
    );
  };

  const showShareModal = (slideset: SlideSet) => {
    if (!selectedNode) return;
    showModal(
      <ShareModal
        id="share-modal"
        moduleId={selectedModule.id}
        nodeId={selectedNode.id}
        slideSets={[slideset]}
        onSubmitted={refetch}
      />,
    );
  };

  function ActionsMenu() {
    if (!showActionMenu || !identity.isTeacherAlike) return null;

    return (
      <ActionMenuButton
        disabled={disabled}
        menuProps={{
          ariaLabel: t('PAGES.TOC_LEVEL.ACTION_MENU.A11Y_LABEL'),
        }}
        buttonProps={{
          ariaLabel: t('PAGES.TOC_LEVEL.ACTION_MENU.BUTTON.A11Y_LABEL'),
        }}
      >
        {handleToggle => (
          <>
            <DropdownMenu.Item
              text={t('PAGES.TOC_LEVEL.ACTION_MENU.OPTIONS.PRESENT_SLIDE_SET.LABEL')}
              textIcon={<MaterialPresentationPresentModusIcon />}
              role="menuitem"
              href={slideSetUrl}
              target="_blank"
              rel="noopener noreferrer"
              as="link"
              onClick={() => captureOpened(slideSet.id, subLocationId)}
            />
            {actsAs === 'teacher' && (
              <>
                <DropdownMenu.Item
                  text={t('PAGES.TOC_LEVEL.ACTION_MENU.OPTIONS.OVERVIEW_SLIDE_SET.LABEL')}
                  textIcon={<Details />}
                  role="menuitem"
                  href={slideSetOverviewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  as="link"
                  showSeparator={true}
                  onClick={() => captureOverviewOpened(slideSet.id, subLocationId)}
                />
                {slideSet.userId && (
                  <DropdownMenu.Item
                    text={t('PAGES.TOC_LEVEL.ACTION_MENU.OPTIONS.EDIT_SLIDE_SET.LABEL')}
                    textIcon={<Edit />}
                    role="menuitem"
                    href={slideSetEditUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    as="link"
                    onClick={() => {
                      captureEditOpened(slideSet.id, subLocationId);
                      handleToggle();
                    }}
                  />
                )}
                <DropdownMenu.Item
                  text={t('PAGES.TOC_LEVEL.ACTION_MENU.OPTIONS.DUPLICATE_SLIDE_SET.LABEL')}
                  textIcon={<Duplicate />}
                  role="menuitem"
                  href={buildCopySlideSetUrl(
                    selectedModule.id,
                    slideSet.nodeId,
                    slideSet.id,
                    slideSet.versionId,
                    provider,
                    subLocationId,
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  as="link"
                  onClick={() => handleToggle()}
                />
                <DropdownMenu.Item
                  text={t('PAGES.TOC_LEVEL.ACTION_MENU.OPTIONS.PRINT_SLIDE_SET.LABEL')}
                  textIcon={<Print />}
                  role="menuitem"
                  href={`${slideSetOverviewUrl}open-print-modal=true`}
                  target="_blank"
                  rel="noopener noreferrer"
                  as="link"
                  onClick={() => handleToggle()}
                />
                {slideSet.userId && canShare && (
                  <DropdownMenu.Item
                    text={t('PAGES.TOC_LEVEL.ACTION_MENU.OPTIONS.SHARE_MATERIAL.LABEL')}
                    textIcon={<Share />}
                    as="button"
                    role="menuitem"
                    onClick={() => {
                      showShareModal(slideSet);
                      handleToggle();
                    }}
                  />
                )}
                {slideSet.userId && onDelete && (
                  <DropdownMenu.Item
                    prominence="danger"
                    text={t('PAGES.TOC_LEVEL.ACTION_MENU.OPTIONS.DELETE_SLIDE_SET.LABEL')}
                    textIcon={<Trash />}
                    as="button"
                    role="menuitem"
                    showSeparator={true}
                    onClick={() => {
                      showDeleteModal(slideSet);
                      handleToggle();
                    }}
                  />
                )}
              </>
            )}
          </>
        )}
      </ActionMenuButton>
    );
  }

  const actionArea = (canBePinned || !showSelection) && (
    <ButtonGroup
      {...buildGroupProps(
        <>
          {canBePinned && (
            <PinnedMaterialButton
              key="pinned-material-button"
              type="slide-set"
              materialId={slideSet.id}
              disabled={disabled}
            />
          )}
          {!showSelection && <ActionsMenu key="actions-menu" />}
        </>,
      )}
      size="sm"
    />
  );

  if (showSelection) {
    const isSelected = selectedSlideSets.some(s => s.id === slideSet.id);
    return (
      <List.Item
        title={slideSet.name}
        actionArea={actionArea}
        pillGroupProps={pillGroupProps}
        as="checkbox"
        checked={isSelected}
        disabled={disabled}
        onClick={() => (isSelected ? removeSlideSets([slideSet]) : addSlideSets([slideSet]))}
      />
    );
  }

  return (
    <List.Item
      title={slideSet.name}
      icon={<MaterialIcon materialType="slide-set" isUserSlideSet={Boolean(slideSet.userId)} />}
      actionArea={actionArea}
      pillGroupProps={pillGroupProps}
      as="link"
      href={slideSetUrl}
      target="_blank"
      rel="noopener noreferrer"
      disabled={disabled}
      onClick={() => captureOpened(slideSet.id, subLocationId)}
    />
  );
}
